import { Dispatch } from 'redux'
import { nanoid } from 'nanoid'

import {
  create,
  destroy,
  getOne,
  getOnePublic,
  putImage,
  QueryParams,
  update,
  putFunnelStages,
  getOneApiAccess,
  skipFetchDataOnUpdate,
  TPostDashboardWidgetBody
} from 'redux/api/Widgets'

import * as UserEvents from 'redux/api/internal/UserEvents'

import { useThunkActions } from 'redux/actions/useThunkActions'

import * as actions from './actions'
import * as widgetActions from '../actions'

import { Action } from 'types/GlobalUserEvents'
import {
  WidgetType,
  WidgetPatchBody,
  TDashboardWidgetFullyLoaded,
  TCoreWidget
} from 'types/GlobalWidget'

import { toggleSnackbarOpen } from 'redux/actions/Snackbar'

import { SnackbarMessage } from 'types/GlobalSnackbar'
import { TPutFunnelStages } from 'redux/api/Widgets/types'
import { selectGlobalFilterForApi } from 'redux/selectors/GlobalFilter'
import { ApplicationState } from 'redux/Stores/types'
import { selectWidgetQueryParams } from 'redux/selectors/DashboardWidgets'
import { parseApiError } from 'utils/errorParser'

function tryPutFunnelStages(id: string, data: TPutFunnelStages) {
  return (dispatch: Dispatch) => {
    dispatch(actions.putFunnelStages())

    putFunnelStages(id, data)
      .then((response) => {
        dispatch(
          actions.putFunnelStagesSuccess({
            id,
            data: response.data.data
          })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetUpdated))
      })
      .catch(() => {
        dispatch(actions.putFunnelStagesFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetUpdatedFailed))
      })
  }
}

// Shared core function for fetching dashboard widgets
function tryGetDashboardWidgetCore(
  widget: TCoreWidget,
  dashboardId: string,
  getOneFunction: () => Promise<{ data: { data: TDashboardWidgetFullyLoaded } }>
) {
  return (dispatch: Dispatch) => {
    // No need to run getOne for static widget types
    if (
      widget.type === WidgetType.TEXT ||
      widget.type === WidgetType.IMAGE ||
      widget.type === WidgetType.COMMENT
    ) {
      return
    }

    const fetchId = nanoid()

    dispatch(actions.getOne({ id: widget.id, dashboardId, fetchId }))

    getOneFunction()
      .then((response) => {
        dispatch(actions.getOneSuccess({ data: response.data.data, fetchId }))
      })
      .catch((error) => {
        const errorDetails = parseApiError(error)

        dispatch(
          actions.getOneFailed({
            widgetId: widget.id,
            fetchId,
            error: errorDetails
          })
        )
      })
  }
}

function tryGetOneDashboardWidget(widget: TCoreWidget, dashboardId: string) {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const globalFilter = selectGlobalFilterForApi(getState())
    const queryParam = selectWidgetQueryParams(getState(), widget.id)

    return tryGetDashboardWidgetCore(widget, dashboardId, () =>
      getOne(widget.id, queryParam, globalFilter)
    )(dispatch)
  }
}

function tryGetOnePublicDashboardWidget(
  widget: TCoreWidget,
  dashboardId: string,
  rawQueryParams = ''
) {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    const queryParam = selectWidgetQueryParams(getState(), widget.id)

    return tryGetDashboardWidgetCore(widget, dashboardId, () =>
      getOnePublic(widget.id, queryParam, rawQueryParams)
    )(dispatch)
  }
}
function tryGetOneApiAccessDashboardWidget(
  widget: TCoreWidget,
  token: string,
  dashboardId: string,
  companies: string[],
  rawQueryParams = ''
) {
  return (dispatch: Dispatch, getState: () => ApplicationState) => {
    let queryParam = selectWidgetQueryParams(getState(), widget.id)

    // Add companies to query params if provided
    if (companies.length > 0) {
      queryParam = {
        ...(queryParam || {}),
        companies
      }
    }

    return tryGetDashboardWidgetCore(widget, dashboardId, () =>
      getOneApiAccess(widget.id, token, queryParam, rawQueryParams)
    )(dispatch)
  }
}

function tryCreateDashboardWidget(
  body: TPostDashboardWidgetBody,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create(body)
      .then((response) => {
        UserEvents.log({
          action: Action.WIDGET_CREATED,
          context: null,
          dashboard_id: body.dashboard_id,
          dashboard_group_id: null
        })

        if ('slices' in body && body.slices.length > 0) {
          UserEvents.log({
            action: Action.KPI_OPTION_SLICE_CREATED,
            context: null,
            dashboard_id: null,
            dashboard_group_id: null
          })
        }

        dispatch(
          actions.createSuccess({
            data: response.data.data,
            available_space: response.data.available_space
          })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetCreated))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.createFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetCreatedFailed))

        callback?.(true)
      })
  }
}

function tryUpdateDashboardWidget(
  id: string,
  body: WidgetPatchBody,
  periodFilterIsSet = false
) {
  return (dispatch: Dispatch) => {
    dispatch(widgetActions.update())

    update(id, body)
      .then((response) => {
        UserEvents.log({
          action: Action.WIDGET_EDITED,
          context: null,
          dashboard_id: null,
          dashboard_group_id: null
        })

        dispatch(
          widgetActions.updateSuccess({
            data: response.data.data,
            skipFetchData: skipFetchDataOnUpdate(body, periodFilterIsSet)
          })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetUpdated))
      })
      .catch(() => {
        dispatch(widgetActions.updateFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetUpdatedFailed))
      })
  }
}

function tryDeleteDashboardWidget(id: string, dashboardId: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then((response) => {
        UserEvents.log({
          action: Action.WIDGET_DELETED,
          context: null,
          dashboard_id: null,
          dashboard_group_id: null
        })

        dispatch(
          actions.destroySuccess({
            id,
            dashboardId,
            available_space: response.data.available_space
          })
        )

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetDeleted))
      })
      .catch(() => {
        dispatch(actions.destroyFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetDeletedFailed))
      })
  }
}

function tryUploadWidgetImage(
  id: string,
  image: Blob,
  name: string,
  queryParam: QueryParams | null
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.uploadImage())

    putImage(id, image, name, queryParam)
      .then((response) => {
        dispatch(actions.uploadImageSuccess(response.data.data))

        dispatch(toggleSnackbarOpen(SnackbarMessage.FileUploaded))
      })
      .catch(() => {
        dispatch(actions.uploadImageFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.FileUploadedFailed))
      })
  }
}

export const useDashboardWidgetActions = () => {
  return {
    tryCreateDashboardWidget: useThunkActions(tryCreateDashboardWidget),
    tryPutFunnelStages: useThunkActions(tryPutFunnelStages),
    tryUpdateDashboardWidget: useThunkActions(tryUpdateDashboardWidget),
    tryDeleteDashboardWidget: useThunkActions(tryDeleteDashboardWidget),
    tryUploadWidgetImage: useThunkActions(tryUploadWidgetImage),
    tryGetOneDashboardWidget: useThunkActions(tryGetOneDashboardWidget),
    tryGetOnePublicDashboardWidget: useThunkActions(
      tryGetOnePublicDashboardWidget
    ),
    tryGetOneApiAccessDashboardWidget: useThunkActions(
      tryGetOneApiAccessDashboardWidget
    )
  }
}
